import { LessonController, LessonPhases } from './lesson';
import { QuizOverlay } from './quiz/overlay';
import { showXpPopup, flashCard } from './quiz/feedback';
import { getUiSoundPlayer } from './ui-sounds';
import { loadBank, sampleBank } from './content/bank-loader';
import { loadProgress, saveProgress, createFreshProgress, addFactionXP, skillUnlocked } from '../shared/progress';
import { findOrCreateKP, recordAttempt } from '../shared/student-kp';
import { FACTIONS, factionForSubject } from '../shared/factions';
import { SKILL_CATALOG } from '../shared/skills';
import { mountEscapeButton } from './escape-button';
import { isProMode } from './settings';
import type { LearnDoneCallback } from './app';

export async function runSocialLesson(container: HTMLElement, kpId: string, onDone: LearnDoneCallback): Promise<void> {
  let url: string;
  if (kpId === 'social.community.basic') url = '/data/social-community-翰林.json';
  else if (kpId === 'social.taiwan.geography') url = '/data/social-taiwan-geography-翰林.json';
  else if (kpId === 'social.hometown.basic') url = '/data/social-hometown-翰林.json';
  else if (kpId === 'social.custom.festival') url = '/data/social-festival-翰林.json';
  else if (kpId === 'social.life.rule') url = '/data/social-life-rule-翰林.json';
  else if (kpId === 'social.traffic.basic') url = '/data/social-transport-翰林.json';
  else if (kpId === 'social.environment.basic') url = '/data/social-environment-翰林.json';
  // ── 三下 KPs that now have bank files (flipped from wip → ready) ──
  else if (kpId === 'social.family.roles') url = '/data/social-family-roles-翰林.json';
  else if (kpId === 'social.local.environment') url = '/data/social-local-environment-翰林.json';
  // ── 三上 backport KP (grade: 3.1) ──────────────────────────────
  else if (kpId === 'social.self.family.up') url = '/data/social-self-up-翰林.json';
  else if (kpId === 'social.school.life.up') url = '/data/social-school-up-翰林.json';
  else if (kpId === 'social.time.place.up') url = '/data/social-time-place-up-翰林.json';
  else if (kpId === 'social.rules.basic.up') url = '/data/social-rules-up-翰林.json';
  // ── stage6 wire: meow911 段考真題池(對齊既有 chapter)─────────────
  else if (kpId === 'social.community.basic.meow911') url = '/data/meow911-social-ch1-community.json';
  else if (kpId === 'social.taiwan.geography.meow911') url = '/data/meow911-social-ch2-geography.json';
  else if (kpId === 'social.hometown.basic.meow911') url = '/data/meow911-social-ch3-hometown.json';
  else if (kpId === 'social.life.rule.meow911') url = '/data/meow911-social-ch4-rule.json';
  else if (kpId === 'social.traffic.basic.meow911') url = '/data/meow911-social-ch5-traffic.json';
  else if (kpId === 'social.environment.basic.meow911') url = '/data/meow911-social-ch6-environment.json';
  else if (kpId === 'social.meow911.misc') url = '/data/meow911-social-misc-pool.json';
  else { throw new Error(`Unknown social KP: ${kpId}`); }

  const bank = await loadBank(url);
  // Sample 10 of 15 questions, distinct surfaceVariants. Split: 2 warmup + 3 guide + 5 challenge
  const sampled = sampleBank(bank, 10);
  const phases: LessonPhases = {
    warmup: sampled.slice(0, 2),
    guide: sampled.slice(2, 5),
    challenge: sampled.slice(5, 10),
  };

  // Load (or create) progress early so we can record KP attempts during the lesson
  let progress = loadProgress();
  if (!progress) progress = createFreshProgress('指揮官');
  // v24 教學專業模式 — boss directive 2026-05-22: 教學要專業.
  const pro = isProMode(progress);
  const kp = findOrCreateKP(progress, kpId, 'social');

  const lesson = new LessonController(phases, 2, (correct) => {
    recordAttempt(kp, correct ? 'correct' : 'wrong', new Date().toISOString());
    saveProgress(progress!);
  }); // medium difficulty

  let overlay: QuizOverlay | null = new QuizOverlay({
    container,
    proMode: pro,
    onAnswer: (answer: number | string) => {
      const card = container.querySelector('.card') as HTMLElement | null;
      const result = lesson.submitAnswer(answer);
      if (!pro && card) flashCard(card, result.correct ? 'correct' : 'wrong');
      if (result.correct) {
        if (!pro) getUiSoundPlayer().play('answer-correct', 0.5);
      } else {
        if (!pro) getUiSoundPlayer().play('answer-wrong', 0.4);
      }
      if (!pro) {
        if (result.xp > 0) {
          const faction = FACTIONS[factionForSubject('social')];
          getUiSoundPlayer().play('xp-popup', 0.35);
          showXpPopup(`+${Math.round(result.xp)} ${faction.emoji}`, '#ef9a9a');
        } else if (!result.correct) {
          showXpPopup('沒關係 +2', '#80cbc4');
        }
      }
      setTimeout(() => {
        if (lesson.state === 'done') finish();
        else renderCurrent();
      }, result.correct ? 500 : 900);
    },
  });

  const unmountEscape = mountEscapeButton(() => {
    overlay?.destroy();
    overlay = null;
    unmountEscape();
    onDone({ newUnlocks: [], exited: true });
  });

  function renderCurrent(): void {
    if (!overlay) return;
    overlay.render(lesson.currentQuestion(), lesson.state, lesson.positionInPhase());
  }

  function finish(): void {
    unmountEscape();
    overlay?.destroy();
    overlay = null;
    // Reuse the progress object already held in closure (has KP updates from recordKP callbacks)
    const faction = factionForSubject('social');
    const xp = Math.round(lesson.totalXP);
    addFactionXP(progress!, faction, xp);
    // Generic unlock detection
    const newUnlocks: string[] = [];
    for (const skill of SKILL_CATALOG) {
      if (skill.faction === 'builtin') continue;
      if (skillUnlocked(progress!, skill.id)) continue;
      if (progress!.factionXP[skill.faction] >= skill.cost) {
        progress!.unlockedSkills.push(skill.id);
        newUnlocks.push(skill.id);
      }
    }
    saveProgress(progress!);
    onDone({ newUnlocks });
  }

  renderCurrent();
}
